import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup'; // Import yup resolver
import * as yup from 'yup';
import { NearByPlace } from '../models/projectModel';
import {
  createNearbyPlacesEndpoint,
  getNearbyPlacesEndpoint,
  updateNearbyPlacesEndpoint,
} from '../services/investorService';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { ErrorAlert, SuccessAlert } from '../../../models/alertModel';
import { useNavigate } from 'react-router-dom';

const useNearByPlace = () => {
  const navigate = useNavigate();
  const [sweetAlert, setSweetAlert] = useState<boolean>(false);
  const [alertProps, setAlertProps] = useState<SuccessAlert | ErrorAlert>({
    success: true,
    title: '',
    text: '',
    onConfirm: () => setSweetAlert(false),
    onCancel: () => setSweetAlert(false),
  });
  // State to store created results
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [results, setResults] = useState<NearByPlace[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null); // Track the ID of the row being edited
  const { callEndpoint, isLoading } = useCallApiAndLoad();

  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '', // Default value for the name field
    },
    resolver: yupResolver(
      yup
        .object({
          name: yup.string().required('Nombre es requerido'),
        })
        .required(),
    ),
  });

  // State to manage search query
  const [searchQuery, setSearchQuery] = useState(''); // State for search query
  // Filter results based on search query
  const filteredResults = results.filter((result) =>
    result.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Handle form submission
  const onSubmit = async (data: { name: string }) => {
    if (editingId !== null) {
      // Call API to update the entry
      const { status } = await callEndpoint(
        updateNearbyPlacesEndpoint(editingId.toString(), data.name, token),
      );
      if (status !== 200) {
        setAlertProps((state) => ({
          ...state,
          success: false,
          danger: true,
          title: 'Error',
          text: 'Lo sentimos, Sitio de interés no pudo ser actualizado',
        }));
        setSweetAlert(true);
        return;
      }
      setEditingId(null); // Clear editing state
      // Update existing entry
      setResults((prevResults) =>
        prevResults.map((result) =>
          result.id === editingId ? { ...result, name: data.name } : result,
        ),
      );
      setAlertProps((state) => ({
        ...state,
        success: true,
        danger: false,
        title: 'OK',
        text: 'Sitio de interés actualizado correctamente',
      }));
      setSweetAlert(true);
      reset(); // Reset the form after submission
    } else {
      // Call API to create a new entry
      const { status } = await callEndpoint(
        createNearbyPlacesEndpoint(data.name, token),
      );
      if (status !== 201) {
        setAlertProps((state) => ({
          ...state,
          success: false,
          danger: true,
          title: 'Error',
          text: 'Lo sentimos, Sitio de interés no pudo ser creado',
        }));
        setSweetAlert(true);
        return;
      }
      setAlertProps((state) => ({
        ...state,
        onConfirm: () => navigate(0),
        success: true,
        danger: false,
        title: 'OK',
        text: 'Sitio de interés creado correctamente',
      }));
      setSweetAlert(true);
    }
  };

  // Handle edit button click
  const handleEdit = (id: string) => {
    if (results && results.length === 0) return; // Check if results are available
    const resultToEdit = results.find((result) => result.id === id);
    if (resultToEdit) {
      setValue('name', resultToEdit.name); // Fill the form with the selected row's data
      setEditingId(id); // Set the editing ID
    }
  };

  const loadNearPlaces = async () => {
    const { status, data } = await callEndpoint(getNearbyPlacesEndpoint(token));
    if (status !== 200) {
      // setErrorAlert(`Lo sentimos, Sitios de interés no pudieron ser cargados`);
      return;
    }
    const response = fetchResponseAdapter(data);
    setResults(response.data);
  };

  useEffect(() => {
    loadNearPlaces().catch((e) => console.error(e));
  }, []);

  return {
    results,
    handleEdit,
    handleSubmit,
    onSubmit,
    control,
    errors,
    editingId,
    reset,
    setEditingId,
    sweetAlert,
    alertProps,
    isLoading,
    searchQuery,
    setSearchQuery,
    filteredResults,
  };
};

export default useNearByPlace;
